"use strict";
import { SwapEventName } from "@uniswap/analytics-events";
import { popupRegistry } from "components/Popups/registry";
import { PopupType } from "components/Popups/types";
import { formatSwapSignedAnalyticsEventProperties } from "lib/utils/analytics";
import {
  addTransactionBreadcrumb,
  getSwapTransactionInfo,
  handleSignatureStep
} from "state/sagas/transactions/utils";
import { addSignature } from "state/signatures/reducer";
import { SignatureType } from "state/signatures/types";
import { call, put } from "typed-redux-saga";
import { UniswapXOrderStatus } from "types/uniswapx";
import { submitOrder } from "data/apiClients/tradingApi/TradingApiClient";
import { Routing } from "data/tradingApi/__generated__";
import { InterfaceEventNameLocal } from "features/telemetry/constants";
import { sendAnalyticsEvent } from "features/telemetry/send";
import { HandledTransactionInterrupt } from "features/transactions/errors";
import { slippageToleranceToPercent } from "features/transactions/swap/utils/format";
export function* handleUniswapXSignatureStep(params) {
  const { analytics, step, trade } = params;
  const { quote, routing } = trade.quote;
  const orderHash = quote.orderId;
  const chainId = trade.inputAmount.currency.chainId;
  const signatureDetails = getUniswapXSignatureInfo(step, trade, chainId, routing);
  const analyticsParams = {
    trade,
    allowedSlippage: slippageToleranceToPercent(trade.slippageTolerance),
    fiatValues: {
      amountIn: analytics?.token_in_amount_usd,
      amountOut: analytics?.token_out_amount_usd,
      feeUsd: analytics?.fee_usd
    },
    portfolioBalanceUsd: analytics?.total_balances_usd,
    trace: { ...analytics }
  };
  sendAnalyticsEvent(
    InterfaceEventNameLocal.UniswapXSignatureRequested,
    formatSwapSignedAnalyticsEventProperties(analyticsParams)
  );
  const signature = yield* call(handleSignatureStep, params);
  if (Date.now() / 1e3 > step.deadline) {
    throw new HandledTransactionInterrupt("User signed after deadline");
  }
  addTransactionBreadcrumb({ step, data: { routing, ...signatureDetails.swapInfo }, status: "in progress" });
  sendAnalyticsEvent(
    SwapEventName.SWAP_SIGNED,
    formatSwapSignedAnalyticsEventProperties({
      trade,
      allowedSlippage: slippageToleranceToPercent(trade.slippageTolerance),
      fiatValues: {
        amountIn: analytics?.token_in_amount_usd,
        amountOut: analytics?.token_out_amount_usd,
        feeUsd: analytics?.fee_usd
      },
      portfolioBalanceUsd: analytics?.total_balances_usd,
      trace: { ...analytics }
    })
  );
  try {
    yield* call(submitOrder, { signature, quote, routing });
  } catch (error) {
    sendAnalyticsEvent(InterfaceEventNameLocal.UniswapXOrderPostError, {
      ...formatSwapSignedAnalyticsEventProperties(analyticsParams),
      detail: error.message
    });
    throw error;
  }
  sendAnalyticsEvent(
    InterfaceEventNameLocal.UniswapXOrderSubmitted,
    formatSwapSignedAnalyticsEventProperties(analyticsParams)
  );
  yield* put(addSignature(signatureDetails));
  popupRegistry.addPopup({ type: PopupType.Order, orderHash }, orderHash);
}
const ROUTING_TO_SIGNATURE_TYPE_MAP = {
  [Routing.DUTCH_V2]: SignatureType.SIGN_UNISWAPX_V2_ORDER,
  [Routing.DUTCH_V3]: SignatureType.SIGN_UNISWAPX_V3_ORDER,
  [Routing.PRIORITY]: SignatureType.SIGN_PRIORITY_ORDER
  // [Routing.LIMIT_ORDER]: SignatureType.SIGN_LIMIT,
};
function getUniswapXSignatureInfo(step, trade, chainId, routing) {
  const swapInfo = getSwapTransactionInfo(trade);
  return {
    type: ROUTING_TO_SIGNATURE_TYPE_MAP[routing],
    id: step.quote.orderId,
    addedTime: Date.now(),
    chainId,
    offerer: trade.quote.quote.orderInfo.swapper,
    orderHash: trade.quote.quote.orderId,
    status: UniswapXOrderStatus.OPEN,
    swapInfo
  };
}
